import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

export interface AssessmentSession {
  userId: string;
  sessionId: string;
  assessmentState?: string;
  level?: Record<string, AssessmentLevel>;
  [key: string]: any;
}

export interface AssessmentLevel {
  level?: number;
  history?: AssessmentLevelHistory[];
  evaluate?: boolean;
  selected?: Array<string | number> | null;
  [key: string]: any;
}

export interface AssessmentLevelHistory {
  accuracy?: number;
  totalAttempted?: number;
  correctAnswer?: number;
  level?: number;
  [key: string]: any;
}

export interface AssessmentLevelEntry {
  key: string;
  value: AssessmentLevel;
}

@Component({
  selector: 'app-user-score-drawer',
  templateUrl: './user-score-drawer.component.html',
  styleUrls: ['./user-score-drawer.component.css'],
  standalone: false
})
export class UserScoreDrawerComponent implements OnChanges {
  @ViewChild('levelHistoryDialog') levelHistoryDialog?: TemplateRef<unknown>;

  @Input() user: any = null;
  @Input() sessions: AssessmentSession[] = [];
  @Input() loading = false;
  @Input() errorMessage = '';
  @Input() savingStateSessionId: string | null = null;
  @Input() savingAssessmentSessionId: string | null = null;
  @Input() savingAttemptAccess = false;

  @Output() closeDrawer = new EventEmitter<void>();
  @Output() loadDashboard = new EventEmitter<AssessmentSession>();
  @Output() saveState = new EventEmitter<{ sessionId: string; state: string }>();
  @Output() saveAssessment = new EventEmitter<{ session: AssessmentSession; flag: 'CREATE-NEW' | 'UPDATE-OLD' }>();
  @Output() saveAttemptAccess = new EventEmitter<boolean>();

  readonly assessmentStateOptions = ['INPROGRESS', 'COMPLETED', 'FAILED'];
  stateDrafts: Record<string, string> = {};
  editedSessions: Record<string, AssessmentSession> = {};
  selectedLevelEntry: AssessmentLevelEntry | null = null;
  assessmentAttemptBlockedDraft = false;

  constructor(private dialog: MatDialog) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      this.assessmentAttemptBlockedDraft = this.user?.assessmentAttemptBlocked === true;
    }

    if (changes['sessions']) {
      this.stateDrafts = {};
      this.editedSessions = {};

      this.sessions.forEach((session) => {
        this.stateDrafts[session.sessionId] = session.assessmentState || 'INPROGRESS';
      });
    }
  }

  emitSaveAttemptAccess(): void {
    this.saveAttemptAccess.emit(this.assessmentAttemptBlockedDraft);
  }

  getDisplaySession(session: AssessmentSession): AssessmentSession {
    return this.editedSessions[session.sessionId] || session;
  }

  isEditing(sessionId: string): boolean {
    return !!this.editedSessions[sessionId];
  }

  startEdit(session: AssessmentSession): void {
    this.editedSessions[session.sessionId] = this.cloneSession(session);
  }

  cancelEdit(sessionId: string): void {
    delete this.editedSessions[sessionId];
  }

  adjustLevel(session: AssessmentSession, key: string, delta: number): void {
    const editableSession = this.getEditableSession(session);
    const currentLevel = Number(editableSession.level?.[key]?.level ?? 0);
    editableSession.level = {
      ...(editableSession.level || {}),
      [key]: {
        ...(editableSession.level?.[key] || {}),
        level: Math.max(0, currentLevel + delta)
      }
    };
  }

  emitSaveAssessment(session: AssessmentSession, flag: 'CREATE-NEW' | 'UPDATE-OLD'): void {
    const payload = this.getDisplaySession(session);
    this.saveAssessment.emit({
      session: this.cloneSession(payload),
      flag
    });
  }

  emitSaveState(sessionId: string): void {
    this.saveState.emit({
      sessionId,
      state: this.stateDrafts[sessionId] || 'INPROGRESS'
    });
  }

  getLevelEntries(session: AssessmentSession): AssessmentLevelEntry[] {
    const sessionToRender = this.getDisplaySession(session);
    return Object.entries(sessionToRender.level || {})
      .filter(([, value]) => value?.evaluate !== false)
      .map(([key, value]) => ({ key, value }));
  }

  getBehavioralSelectionEntries(session: AssessmentSession): AssessmentLevelEntry[] {
    const sessionToRender = this.getDisplaySession(session);
    return Object.entries(sessionToRender.level || {})
      .filter(([, value]) => value?.evaluate === false)
      .map(([key, value]) => ({ key, value }));
  }

  getBehavioralSelections(value: AssessmentLevel): Array<string | number> {
    return Array.isArray(value?.selected) ? value.selected : [];
  }

  openLevelHistory(levelEntry: AssessmentLevelEntry): void {
    if (!this.levelHistoryDialog) return;

    this.selectedLevelEntry = levelEntry;
    this.dialog.open(this.levelHistoryDialog, {
      width: 'min(680px, 94vw)',
      maxHeight: '88vh',
      autoFocus: false
    });
  }

  onLevelCardKeydown(event: KeyboardEvent, levelEntry: AssessmentLevelEntry): void {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    this.openLevelHistory(levelEntry);
  }

  isCompleted(session: AssessmentSession): boolean {
    return (this.getDisplaySession(session).assessmentState || '').toUpperCase() === 'COMPLETED';
  }

  private getEditableSession(session: AssessmentSession): AssessmentSession {
    if (!this.editedSessions[session.sessionId]) {
      this.startEdit(session);
    }

    return this.editedSessions[session.sessionId];
  }

  private cloneSession(session: AssessmentSession): AssessmentSession {
    return JSON.parse(JSON.stringify(session));
  }
}
